import React, { useEffect, useState } from "react";
import useOnlineStatus from "@/lib/useOnlineStatus";

/**
 * Friendly, kid-first offline banner. Shows a warm reassuring message
 * ("You can still explore!") the moment the connection drops, and a
 * quick "You're back online" flash when it returns. Sits just under
 * the sticky header so it never covers content or nav.
 *
 * Voice read-alouds, live search, and dynamic AI responses stop working
 * when offline — pages, quizzes, coloring, games and cached content
 * still work. This banner sets expectations without alarming kids.
 */
export default function OfflineIndicator() {
  const online = useOnlineStatus();
  const [wasOffline, setWasOffline] = useState(false);
  const [showBackFlash, setShowBackFlash] = useState(false);

  useEffect(() => {
    if (!online) {
      setWasOffline(true);
      return;
    }
    if (wasOffline) {
      // Just came back → show a brief celebratory strip.
      setShowBackFlash(true);
      const t = setTimeout(() => setShowBackFlash(false), 2500);
      return () => clearTimeout(t);
    }
  }, [online, wasOffline]);

  if (!online) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="offline-banner"
        className="sticky top-14 md:top-20 z-30 border-b-4 border-slate-900 bg-amber-200 text-slate-900"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 flex items-center gap-2 text-sm md:text-base font-bold">
          <span className="text-lg" aria-hidden>📴</span>
          <span>
            You&apos;re offline — but you can still explore, color and play what you&apos;ve visited before!
          </span>
        </div>
      </div>
    );
  }

  if (showBackFlash) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="online-banner"
        className="sticky top-14 md:top-20 z-30 border-b-4 border-slate-900 bg-emerald-200 text-slate-900"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 flex items-center gap-2 text-sm md:text-base font-bold">
          <span className="text-lg" aria-hidden>✅</span>
          <span>You&apos;re back online. Everything is ready to go!</span>
        </div>
      </div>
    );
  }

  return null;
}
